import {
  ATypeCardView,
  ATypeAmenity,
  CTypeMap,
  DTypeParlorList,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  GOI_WEB_URL,
  BTypeBreadCrumb,
  sangjoFaqRichSearch,
  RichSearch,
  buildParlorLocalBusinessSchema,
  TrafficInfo,
  HeroDataCard,
  HeroPills,
  FTypeBinsoPriceSummary,
  EstimateSection,
  SupplementarySection,
  ETypeFAQ,
  ETypeCTA,
  customEvent,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";
import { SidoNav } from "@/components/SidoNav";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = parlors
    .filter((res) => targetSidos.includes(res.sido))
    .map((parlor) => ({
      params: {
        companyname: parlor.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const companyname = params?.companyname as string;
  let parlor;
  let district;
  let description = "";

  if (companyname) {
    parlor = parlors.find((parlor) => parlor.companyname === companyname);
    if (parlor) {
      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      const binsoCount = parlor.binsos?.length || 0;
      const binsoPyeongs = parlor.binsos?.map((b: any) => b.pyeong) || [];
      const minPyeong = binsoPyeongs.length
        ? Math.round(Math.min(...binsoPyeongs))
        : 0;
      const maxPyeong = binsoPyeongs.length
        ? Math.round(Math.max(...binsoPyeongs))
        : 0;
      const parkCnt = parlor.amenities?.parkcnt || 0;
      const managedType = parlor.managed_type || "";
      const 무빈소 = parlor.expected_charge_sum?.["무빈소"]
        ? Math.round(parlor.expected_charge_sum["무빈소"] / 10000)
        : 0;
      const 일반장 = parlor.expected_charge_sum?.["일반장"]
        ? Math.round(parlor.expected_charge_sum["일반장"] / 10000)
        : 0;

      const descParts = [
        `${parlor.companyname} 비용, 빈소, 이용시설, 위치 정보를 종합 안내합니다`,
        `공공 데이터 기반 · 고이장례연구소`,
        `주소 : ${parlor.address}`,
        managedType ? `${managedType} 장례식장` : "",
        binsoCount ? `빈소 ${binsoCount}실(${minPyeong}~${maxPyeong}평)` : "",
        무빈소 && 일반장 ? `예상 총비용 ${무빈소}만~${일반장}만원` : "",
        parkCnt ? `주차 ${parkCnt}대` : "",
      ].filter(Boolean);
      description = descParts.join(" | ");
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${parlor?.companyname}, ${parlor?.companyname} 비용, ${parlor?.companyname}평균 예상 비용, ${parlor?.companyname} 무빈소, ${parlor?.companyname} 가족장, ${parlor?.companyname} 추천,
      ${parlor?.companyname} 시설, ${parlor?.companyname} 위치, ${parlor?.companyname} 주소, ${parlor?.companyname} 빈소, ${parlor?.companyname} 후기 ${parlor?.companyname} 영업시간`,
      pageOgTitle: `${parlor?.companyname} 비용, 빈소, 이용시설, 위치 안내 (${district}) | 대구경북 장례정보 [고이장례연구소]`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || "",
      hasError: !parlor,
    },
  };
};

export default function FuneralPlace({
  defaultParlor,
  pageOgTitle,
}: {
  defaultParlor: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const sidoName = FUNERAL_SIDO.get(Number(defaultParlor.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultParlor.sido))?.get(
    Number(defaultParlor.sigungu),
  );
  const binsos = defaultParlor.binsos || [];
  const minPy = binsos.length
    ? Math.min(...binsos.map((b) => b.pyeong || 0))
    : 0;
  const maxPy = binsos.length
    ? Math.max(...binsos.map((b) => b.pyeong || 0))
    : 0;

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        <link
          rel="canonical"
          href={`https://dggb-portal.funeralblog.co.kr/funeral-places/${defaultParlor.id}/`}
          key="canonical"
        />
      </Head>
        <RichSearch data={buildParlorLocalBusinessSchema(defaultParlor)} />
        <RichSearch
          data={{
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "고이장례연구소",
                item: GOI_WEB_URL + "/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "장례식장 추천",
                item: GOI_WEB_URL + "/match/request/",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: `${sidoName} 장례식장`,
                item: `https://dggb-portal.funeralblog.co.kr/price/${defaultParlor.sido}/`,
              },
              {
                "@type": "ListItem",
                position: 4,
                name: `${sigunguName} 장례식장`,
                item: `https://dggb-portal.funeralblog.co.kr/price/${defaultParlor.sido}-${defaultParlor.sigungu}/`,
              },
              {
                "@type": "ListItem",
                position: 5,
                name: defaultParlor.companyname,
              },
            ],
          }}
        />
        <RichSearch data={sangjoFaqRichSearch} />

      <BTypeBreadCrumb
        firstItem={{
          text: "장례식장 추천",
          href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
          event: {
            domain: "dggb",
            path: "funeral_place",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `${sidoName} 장례식장`,
          href: `/price/${defaultParlor.sido}/`,
          event: {
            domain: "dggb",
            path: "funeral_place",
            trigger_and_target: "br_crumb_sido_prlr_list",
          },
        }}
        thirdItem={{
          text: `${sigunguName} 장례식장`,
          href: `/price/${defaultParlor.sido}-${defaultParlor.sigungu}/`,
          event: {
            domain: "dggb",
            path: "funeral_place",
            trigger_and_target: "br_crumb_sigungu_prlr_list",
          },
        }}
        goiHomeEvent={{
          domain: "dggb",
          path: "funeral_place",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />

      {/* 00 Hero */}
      <Box component="header" style={{ padding: "40px 20px 40px" }}>
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "32px",
          }}
          sx={{
            "@media (min-width: 960px)": {
              gridTemplateColumns: "2fr 1fr",
              gap: "56px",
              alignItems: "start",
            },
          }}
        >
          <Box>
            <h1
              style={{
                fontSize: "28px",
                fontWeight: 700,
                lineHeight: 1.3,
                color: "#252525",
                margin: 0,
              }}
            >
              {defaultParlor.companyname}
            </h1>
            <Box
              component="address"
              style={{ marginTop: "8px", fontSize: "13px", color: "#686868", fontStyle: "normal" }}
            >
              {defaultParlor.address}
            </Box>
            <Box
              component="p"
              style={{
                marginTop: "24px",
                fontSize: "16px",
                lineHeight: 1.75,
                color: "#535353",
              }}
            >
              <strong>{defaultParlor.companyname}</strong>({sigunguName})의 빈소 규모와 사용료,
              예상 장례비용을 한눈에 정리했습니다.
              {binsos.length
                ? ` 빈소는 총 ${binsos.length}개로 ${minPy}평부터 ${maxPy}평까지 운영됩니다.`
                : ""}{" "}
              <em style={{ fontStyle: "normal", color: regionColor.c500 }}>
                e하늘 장사정보시스템(보건복지부)
              </em>{" "}
              공개 데이터 기준입니다.
            </Box>
            <Stack
              direction="column"
              gap="10px"
              style={{ marginTop: "32px" }}
            >
              <Box
                component="a"
                href={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound`}
                onClick={() => customEvent({ domain: "dggb", path: "funeral_place", trigger_and_target: "uv_mth_req" })}
                style={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "18px",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: 700,
                  background: regionColor.c400,
                  color: "#fff",
                  textDecoration: "none",
                }}
              >
                {defaultParlor.companyname} 전체 비용 확인하기
              </Box>
            </Stack>
            <Box
              component="p"
              style={{ marginTop: "10px", fontSize: "12px", color: "#888", lineHeight: 1.5, margin: "10px 0 0" }}
            >
              ※ e하늘장사정보시스템을 기반으로 고이장례연구소에서 예상 값을 산출하여 제공해요
            </Box>
          </Box>
          <Box
            sx={{
              "@media (min-width: 960px)": { position: "sticky", top: "84px" },
            }}
          >
            {defaultParlor.images?.[0]?.url && (
              <Box
                component="img"
                src={defaultParlor.images[0].url}
                alt={`${defaultParlor.companyname} 전경`}
                style={{
                  width: "100%",
                  aspectRatio: "16/9",
                  objectFit: "cover",
                  borderRadius: "12px",
                  marginBottom: "16px",
                  display: "block",
                }}
              />
            )}
            <HeroDataCard
              defaultParlor={defaultParlor}
              mainColor={regionColor}
            />
          </Box>
        </Box>
        <HeroPills defaultParlor={defaultParlor} mainColor={regionColor} />
      </Box>

      <ETypeCTA
        title="대구·경북 장례 비용을 투명하게 안내해드려요"
        subtitle="e하늘 장사정보시스템 기반 예상 견적을 무료로 확인하세요"
        href={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound`}
        buttonText="장례 비용 확인하기"
        mainColor={regionColor}
        event={{ domain: "dggb", path: "funeral_place", trigger_and_target: "top_CTA_mth_req" }}
      />

      {/* 02 빈소 상세 명세 리스트 */}
      <Box id="binso">
        <FTypeBinsoPriceSummary
          defaultParlor={defaultParlor}
          mainColor={regionColor}
          ctaHref={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound`}
          ctaText="빈소 가격을 포함한 전체 장례 비용을 확인해보세요"
          event={{ domain: "dggb", path: "funeral_place", trigger_and_target: "uv_expert_mth_req" }}
        />
      </Box>

      {/* 03 예상 비용 */}
      <EstimateSection defaultParlor={defaultParlor} mainColor={regionColor} />

      {/* 03 CTA */}
      <ETypeCTA
        title="대구·경북 장례 비용을 투명하게 안내해드려요"
        subtitle="e하늘 장사정보시스템 기반 예상 견적을 무료로 확인하세요"
        href={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound`}
        buttonText="무료 견적 받아보기"
        mainColor={regionColor}
        event={{ domain: "dggb", path: "funeral_place", trigger_and_target: "CTA_uv_mth_req" }}
      />
      <Box
        component="p"
        style={{ padding: "0 20px 20px", fontSize: "12px", color: "#888", lineHeight: 1.5, margin: 0 }}
      >
        ※ e하늘장사정보시스템을 기반으로 고이장례연구소에서 예상 값을 산출하여 제공해요
      </Box>

      {/* 04 위치 및 교통 */}
      <Stack style={{ background: regionColor.c100, padding: "40px 20px" }}>
        <CTypeMap
          defaultParlor={defaultParlor}
          address={defaultParlor.address}
        />
      </Stack>
      <TrafficInfo defaultParlor={defaultParlor} />

      {/* 05 편의시설 */}
      <ATypeAmenity defaultParlor={defaultParlor} />

      {/* 06 공설/사립 분기 */}
      <SupplementarySection
        defaultParlor={defaultParlor}
        mainColor={regionColor}
        ctaHref={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound`}
        ctaText={`${defaultParlor.companyname} 기준 실제 견적을\n무료로 받아보실 수 있습니다`}
      />

      {/* 07 FAQ */}
      <ETypeFAQ defaultParlor={defaultParlor} mainColor={regionColor} faqKeys={["binso_charge", "total_cost", "sangjo", "parking"]} />

      <SidoNav currentSidoCode={defaultParlor.sido ?? ""} mainColor={regionColor} />
      {/* 주변 장례식장 */}
      <Stack style={{ padding: "40px 20px" }}>
        <DTypeParlorList
          binsoAveragePrice
          sido={defaultParlor.sido}
          sigungu={String(defaultParlor.sigungu)}
          currentParlorId={defaultParlor.id}

          currentLat={Number((defaultParlor as any).latitude)}

          currentLng={Number((defaultParlor as any).longitude)}
          title={`${sidoName}의 다른 장례식장`}
          CardViewType={(res) => (
            <ATypeCardView
              key={res.companyname}
              name={res.companyname}
              href={`/funeral-places/${res.id}/`}
              descriptions={[
                ...(res.distanceLabel ? [`${defaultParlor.companyname}에서 ${res.distanceLabel}`] : []),
                `빈소 평균 비용 : ${Number(res.binso_average_charge).toLocaleString()}원`,
              ]}
              imgSrc={res.images[0] ? res.images[0].url : ""}
              event={{
                domain: "dggb",
                path: "funeral_place",
                trigger_and_target: "uv_prlr_detail",
              }}
            />
          )}
        />
      </Stack>
    </>
  );
}
